/**
 * Payment API functions for Pay-Per-Plan model
 */
import { api } from './client'

export interface PaymentHistory {
  id: string
  amountCents: number
  currency: string
  status: string
  productType: string
  createdAt: string
  completedAt?: string
  invoicePdfUrl?: string
  stripeInvoiceId?: string
}

/**
 * Credit balance response from /api/credits/balance
 */
export interface CreditBalance {
  singleFamilyCredits: number
  multiUnitCredits: number
  totalCredits: number
  lastSyncedAt: string
  recentPurchases: CreditPurchaseDto[]
}

/**
 * Credit purchase DTO
 */
export interface CreditPurchaseDto {
  id: string
  productCode: string
  productName: string
  quantity: number
  remainingCredits: number
  amountCents: number
  formattedAmount: string
  purchasedAt: string
  status: string
}

/**
 * Request to purchase credits
 */
export interface PurchaseCreditsRequest {
  productCode: string
  quantity: number
  successUrl: string
  cancelUrl: string
}

/**
 * Response from purchasing credits
 */
export interface PurchaseCreditsResponse {
  checkoutUrl: string
  sessionId: string
  quantity: number
  amountCents: number
  formattedAmount: string
}

/**
 * Product codes for Pay-Per-Plan
 */
export const PRODUCT_CODES = {
  singleFamily: 'PLAN_SINGLE_FAMILY',
  multiUnit: 'PLAN_MULTI_UNIT',
} as const

/**
 * Stripe price IDs (matching backend CatalogSeeder)
 */
export const STRIPE_PRICES = {
  singleFamily: 'price_single_family_4500',
  multiUnit: 'price_multi_unit_7500',
} as const

/**
 * Product pricing info
 */
export const PRODUCT_INFO = {
  [PRODUCT_CODES.singleFamily]: {
    name: 'Single-Family Plan Credit',
    shortName: 'Single-Family',
    description: 'For single-family homes with 1 dwelling unit',
    pricePerCredit: 4500, // $45.00
    formattedPrice: '$45',
  },
  [PRODUCT_CODES.multiUnit]: {
    name: 'Multi-Unit Plan Credit',
    shortName: 'Multi-Unit',
    description: 'For buildings with 2+ dwelling units',
    pricePerCredit: 7500, // $75.00
    formattedPrice: '$75',
  },
} as const

/**
 * Get product info from product code
 */
export function getProductInfo(productCode: string) {
  return PRODUCT_INFO[productCode as keyof typeof PRODUCT_INFO] ?? null
}

/**
 * Purchase credits for offline use
 */
export async function purchaseCredits(request: PurchaseCreditsRequest) {
  return api.post<PurchaseCreditsResponse>('/api/credits/purchase', request)
}

/**
 * Get current credit balance
 */
export async function getCreditBalance() {
  return api.get<CreditBalance>('/api/credits/balance')
}

/**
 * Get payment history
 */
export async function getPaymentHistory() {
  return api.get<PaymentHistory[]>('/api/payments/history')
}

/**
 * Format amount in cents to currency string
 */
export function formatAmount(amountCents: number, currency: string = 'usd'): string {
  const amount = amountCents / 100
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount)
}

/**
 * Calculate total for credit purchase
 */
export function calculateTotal(productCode: string, quantity: number): {
  totalCents: number
  formatted: string
  unitPrice: string
} {
  const product = getProductInfo(productCode)
  if (!product) {
    return { totalCents: 0, formatted: '$0.00', unitPrice: '$0' }
  }

  const totalCents = product.pricePerCredit * quantity
  return {
    totalCents,
    formatted: formatAmount(totalCents),
    unitPrice: product.formattedPrice,
  }
}

// ============================================
// Legacy subscription types (for billing history)
// ============================================

export interface Subscription {
  id: string
  status: string
  planName: string
  amountCents: number
  interval: 'month' | 'year'
  currentPeriodStart: string
  currentPeriodEnd: string
  cancelAtPeriodEnd: boolean
}

export interface CreateCheckoutRequest {
  priceId: string
  successUrl: string
  cancelUrl: string
}

export interface CreateCheckoutResponse {
  sessionId: string
  checkoutUrl: string
}

/**
 * Payment method (card on file) details
 */
export interface PaymentMethod {
  last4: string
  brand: string
  expMonth: number
  expYear: number
  isExpired: boolean
}

/**
 * Billing portal response
 */
export interface BillingPortalResponse {
  portalUrl: string
}

/**
 * Get payment method on file
 */
export async function getPaymentMethod() {
  return api.get<PaymentMethod | null>('/api/payments/payment-method')
}

/**
 * Open Stripe billing portal
 */
export async function openBillingPortal(returnUrl: string) {
  return api.post<BillingPortalResponse>('/api/payments/billing-portal', { returnUrl })
}

/**
 * Get current subscription (legacy - for existing subscribers)
 */
export async function getSubscription() {
  return api.get<Subscription | null>('/api/payments/subscription')
}
